import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Debug logs
console.log('🔍 Supabase URL:', supabaseUrl);
console.log('🔍 Supabase Key:', supabaseAnonKey ? 'Present' : 'Missing');

if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Missing Supabase environment variables!');
    console.error('URL:', supabaseUrl);
    console.error('Key:', supabaseAnonKey);
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper functions for authentication
export const authHelpers = {
    // Sign up a new user
    signUp: async (email, password, userData = {}) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: userData,
                // Skip email confirmation - user can log in immediately
                emailRedirectTo: window.location.origin + '/login'
            }
        });
        return { data, error };
    },

    // Sign in
    signIn: async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });
        return { data, error };
    },

    // Sign out
    signOut: async () => {
        const { error } = await supabase.auth.signOut();
        return { error };
    },

    // Get current user
    getCurrentUser: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        return user;
    },

    // Get current user profile
    getCurrentProfile: async () => {
        const user = await authHelpers.getCurrentUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

        return { data, error };
    },

    // Check if user is admin
    isAdmin: async () => {
        const { data } = await authHelpers.getCurrentProfile();
        return data?.role === 'admin';
    }
};

// Helper functions for database operations
export const dbHelpers = {
    // Practitioners
    practitioners: {
        getAll: async () => {
            const { data, error } = await supabase
                .from('practitioners')
                .select('*')
                .order('name');
            return { data, error };
        },

        getById: async (id) => {
            const { data, error } = await supabase
                .from('practitioners')
                .select('*')
                .eq('id', id)
                .single();
            return { data, error };
        },

        create: async (practitioner) => {
            const { data, error } = await supabase
                .from('practitioners')
                .insert([practitioner])
                .select()
                .single();
            return { data, error };
        },

        update: async (id, updates) => {
            const { data, error } = await supabase
                .from('practitioners')
                .update(updates)
                .eq('id', id)
                .select()
                .single();
            return { data, error };
        },

        delete: async (id) => {
            const { error } = await supabase
                .from('practitioners')
                .delete()
                .eq('id', id);
            return { error };
        }
    },

    // Cases
    cases: {
        getAll: async () => {
            const { data, error } = await supabase
                .from('cases')
                .select('*, practitioner:practitioners(*)')
                .order('created_at', { ascending: false });
            return { data, error };
        },

        getById: async (id) => {
            const { data, error } = await supabase
                .from('cases')
                .select('*, practitioner:practitioners(*)')
                .eq('id', id)
                .single();
            return { data, error };
        },

        create: async (caseData) => {
            const { data, error } = await supabase
                .from('cases')
                .insert([caseData])
                .select()
                .single();
            return { data, error };
        },

        update: async (id, updates) => {
            const { data, error } = await supabase
                .from('cases')
                .update(updates)
                .eq('id', id)
                .select()
                .single();
            return { data, error };
        },

        delete: async (id) => {
            const { error } = await supabase
                .from('cases')
                .delete()
                .eq('id', id);
            return { error };
        }
    },

    // Invoices
    invoices: {
        getAll: async () => {
            const { data, error } = await supabase
                .from('invoices')
                .select('*, practitioner:practitioners(*)')
                .order('created_at', { ascending: false });
            return { data, error };
        },

        getById: async (id) => {
            const { data, error } = await supabase
                .from('invoices')
                .select('*, practitioner:practitioners(*)')
                .eq('id', id)
                .single();
            return { data, error };
        },

        create: async (invoice) => {
            const { data, error } = await supabase
                .from('invoices')
                .insert([invoice])
                .select()
                .single();
            return { data, error };
        },

        update: async (id, updates) => {
            const { data, error } = await supabase
                .from('invoices')
                .update(updates)
                .eq('id', id)
                .select()
                .single();
            return { data, error };
        },

        delete: async (id) => {
            const { error } = await supabase
                .from('invoices')
                .delete()
                .eq('id', id);
            return { error };
        }
    },

    // Payments
    payments: {
        create: async (payment) => {
            const { data, error } = await supabase
                .from('payments')
                .insert([payment])
                .select()
                .single();
            return { data, error };
        },

        getByInvoice: async (invoiceId) => {
            const { data, error } = await supabase
                .from('payments')
                .select('*')
                .eq('invoice_id', invoiceId)
                .order('payment_date', { ascending: false });
            return { data, error };
        }
    }
};

export default supabase;
